import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import { Box, Typography } from '@mui/material'
import createSvgIcon from '@mui/material/utils/createSvgIcon'
import { useState } from 'react'

export type ConnectedSystem = {
	id: string
	name: string
	code: string
	env: 'PROD' | 'TEST' | 'DEV'
	owner: string
	tables: number
	color: string
}

export const CONNECTED_SYSTEMS: ConnectedSystem[] = [
	{
		id: 'gasnet',
		name: 'Gasnet Data platform',
		code: 'gasnet',
		env: 'PROD',
		owner: 'DW_CORE',
		tables: 412,
		color: '#E03930',
	},
	{
		id: 'distribuce',
		name: 'Distribuce CZ DWH',
		code: 'distribuce',
		env: 'PROD',
		owner: 'DW_DIST',
		tables: 268,
		color: '#163B6E',
	},
	{
		id: 'trading',
		name: 'Trading & Settlement',
		code: 'trading',
		env: 'TEST',
		owner: 'DW_TRD',
		tables: 97,
		color: '#0F9D58',
	},
]

const ENV_PILL: Record<string, [string, string]> = {
	PROD: ['#0F9D58', '#E6F4EA'],
	TEST: ['#C26401', '#FEF2DC'],
	DEV: ['#2A6FDB', '#E5EEFB'],
}

/** Lucide-style database icon from MetaCroc prototype. */
const DatabaseIcon = createSvgIcon(
	<>
		<ellipse
			cx="12"
			cy="5"
			rx="9"
			ry="3"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M3 5v14a9 3 0 0 0 18 0V5"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M3 12a9 3 0 0 0 18 0"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</>,
	'Database',
)

const Pill = ({ value }: { value: string }) => {
	const [fg, bg] = ENV_PILL[value] || ['#9A9A9A', '#EDEDED']
	return (
		<Box
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: '5px',
				px: '8px',
				py: '2px',
				borderRadius: '999px',
				bgcolor: bg,
				color: fg,
				fontSize: '10px',
				fontWeight: 700,
				letterSpacing: '.04em',
				textTransform: 'uppercase',
			}}
		>
			{value}
		</Box>
	)
}

export const SystemSettingsLayout = () => {
	const [systemId, setSystemId] = useState('gasnet')

	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				height: '100%',
				overflowY: 'auto',
				bgcolor: '#FBFBFC',
			}}
		>
			<Box sx={{ maxWidth: 1100, mx: 'auto', p: '28px 32px 60px' }}>
				<Box sx={{ mb: '6px', fontSize: '11.5px', color: '#9A9A9A', letterSpacing: '.02em' }}>
					MetaCroc › System settings
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '20px' }}>
					<Box
						sx={{
							width: 36,
							height: 36,
							borderRadius: '10px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							bgcolor: 'rgba(31,73,84,0.08)',
							color: '#1F4954',
						}}
					>
						<TuneOutlinedIcon sx={{ fontSize: 20 }} />
					</Box>
					<Box sx={{ flex: 1 }}>
						<Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1F4954' }}>
							System settings
						</Typography>
						<Typography sx={{ fontSize: '12.5px', color: '#9A9A9A' }}>
							Connected systems and their configuration
						</Typography>
					</Box>
				</Box>

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
					{CONNECTED_SYSTEMS.map((s) => {
						const active = s.id === systemId
						return (
							<Box
								key={s.id}
								sx={{
									border: '1px solid',
									borderColor: active ? '#1F4954' : '#E8E8E8',
									borderRadius: '12px',
									bgcolor: '#FFF',
									p: '16px 18px',
									display: 'flex',
									alignItems: 'center',
									gap: '14px',
									boxShadow: active ? '0 1px 6px rgba(31,73,84,0.10)' : 'none',
								}}
							>
								<Box
									sx={{
										width: 40,
										height: 40,
										borderRadius: '10px',
										flex: '0 0 auto',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										bgcolor: `${s.color}1A`,
										color: s.color,
									}}
								>
									<DatabaseIcon sx={{ fontSize: 22 }} />
								</Box>
								<Box sx={{ flex: 1, minWidth: 0 }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
										<Box sx={{ fontSize: '15px', fontWeight: 700, color: '#1F4954' }}>{s.name}</Box>
										<Pill value={s.env} />
										{active ? (
											<Box
												sx={{
													fontSize: '9.5px',
													fontWeight: 700,
													color: '#0F9D58',
													bgcolor: '#E6F4EA',
													borderRadius: '4px',
													px: '6px',
													py: '2px',
													letterSpacing: '.04em',
												}}
											>
												ACTIVE
											</Box>
										) : null}
									</Box>
									<Box sx={{ fontSize: '12px', color: '#9A9A9A', mt: '3px' }}>
										{s.code} · owner {s.owner} · {s.tables} objects
									</Box>
								</Box>
								{active ? (
									<Box sx={{ fontSize: '12px', color: '#9A9A9A', px: '14px', py: '8px' }}>
										Currently selected
									</Box>
								) : (
									<Box
										onClick={() => setSystemId(s.id)}
										sx={{
											display: 'inline-flex',
											alignItems: 'center',
											gap: '7px',
											px: '14px',
											height: 34,
											borderRadius: '8px',
											border: '1px solid #DCDCDC',
											cursor: 'pointer',
											fontSize: '12.5px',
											fontWeight: 600,
											color: '#1F4954',
											'&:hover': {
												bgcolor: 'rgba(31,73,84,0.06)',
												borderColor: '#1F4954',
											},
										}}
									>
										<SwapHorizOutlinedIcon sx={{ fontSize: 14 }} />
										Switch
									</Box>
								)}
							</Box>
						)
					})}
				</Box>
			</Box>
		</Box>
	)
}
